/**
 * @fileoverview A custom hook to encapsulate the taskflow navigation logic, including stepping
 * forward and back, skipping pages, and tracking which pages should be disabled.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */

/**
 * @dict
 * @typedef {{
 *   setPage: function(string): void,
 *   nextPage: function(): void,
 *   prevPage: function(): void,
 *   skipPage: function(boolean, boolean, boolean): void,
 *   resetPage: function(string): void,
 * }}
 */
export let PageControls;

/**
 * @param {!Array<!Array<string>>} pageIds
 * @returns {{
 *   currentPage: string,
 *   pageStates: Map<string, string>,
 *   controls: PageControls,
 * }}
 */
export function useNavigation(pageIds) {
  const [state, dispatch] = React.useReducer(onDispatch, {
    pages: getPages(pageIds),
    index: 0,
  });

  const pageStates = React.useMemo(() => getPageStates(state.pages), [state.pages]);

  const setPage = React.useCallback(id => dispatch({type: 'set', id}), []);
  const nextPage = React.useCallback(() => dispatch({type: 'next'}), []);
  const prevPage = React.useCallback(() => dispatch({type: 'prev'}), []);
  const skipPage = React.useCallback((entireSection, skipped, redirect) => dispatch({type: 'skip', entireSection, skipped, redirect}), []);
  const resetPage = React.useCallback(id => dispatch({type: 'reset', id}), []);

  return {
    currentPage: state.pages[state.index].id,
    pageStates,
    controls: {
      setPage,
      nextPage,
      prevPage,
      skipPage,
      resetPage,
    },
  };
}

function onDispatch(state, action) {
  switch (action.type) {
    // Any page can be navigated to directly. It is up to the parent logic to control when specific
    // steps may can be navigated to (such as if a subsequent step is disabled). Setting also has
    // special behavior when the set section has been skipped; the subsequent skipped pages are
    // also returned to an unskipped state.
    case 'set': {
      const pages = [...state.pages];
      const index = state.pages.findIndex(p => p.id === action.id);

      if (index < 0) {
        return state;
      } else {
        pages[index].disabled = false;

        const section = pages[index].section;
        if (pages[index].skipped) {
          let fixIndex = index;
          // If the page is skipped, subsequent pages that are disabled and skipped were part of
          // a section skip; return these to a disabled unskipped state.
          do {
            pages[fixIndex].skipped = false;
            fixIndex = fixIndex + 1;
          } while (fixIndex < pages.length && isPageSectionSkipped(section, fixIndex, pages));
        }

        return {...state, pages, index};
      }
    }
    // Navigate to the subsequent unskipped page. This allows the user to continue to a new page,
    // even though it is initially disabled. Note that the parent logic is responsible for
    // determining when next may be called; this will always move to the next step.
    case 'next': {
      const pages = [...state.pages];
      let index = state.index + 1;

      index = getNextUnskippedIndex(index, pages);
      if (index === pages.length) {
        return state;
      } else {
        pages[index].disabled = false;
        return {...state, pages, index};
      }
    }
    // Navigate to a previously viewed page. This will move past any skipped pages. It should
    // always be possible to navigate to a previous page, unless already at the first page.
    case 'prev': {
      const pages = [...state.pages];
      let index = state.index - 1;

      index = getPrevUnskippedIndex(index, pages);
      if (index < 0) {
        return state;
      } else {
        pages[index].disabled = false;
        return {...state, pages, index};
      }
    }
    // Skip the specified page(s), ignoring them when the user triggers the next/prev methods. Once a
    // step is skipped, it can only be accessed by using `set` to navigate directly to it.
    case 'skip': {
      const pages = [...state.pages];
      const section = pages[state.index].section;

      let index = state.index + 1;

      while (action.entireSection && index < pages.length && pages[index].section === section) {
        // Also disable subsequent steps, so they cannot be returned to directly.
        pages[index].disabled = action.skipped;
        pages[index].skipped = action.skipped;

        index = index + 1;
      }

      index = getNextUnskippedIndex(index, pages);
      if (index === pages.length) {
        return state;
      } else {
        pages[index].disabled = false;
        if (action.redirect) {
          return {...state, pages, index};
        } else {
          return {...state, pages, index: state.index}; // reset the index to avoid redirecting
        }
      }
    }
    // "Reset" the given page; this signifies that the passed page is no longer complete, and will
    // cause the subsequent page to be returned to the disabled state (effectively disabling the
    // remainder of the pages due to `getPageStates`).
    case 'reset': {
      const pages = [...state.pages];
      let index = state.pages.findIndex(p => p.id === action.id);

      if (index < 0) {
        return state;
      } else {
        index = getNextUnskippedIndex(index + 1, pages);

        if (index === pages.length || index === state.index) {
          return state;
        } else {
          pages[index].disabled = true;
          return {...state, pages};
        }
      }
    }
  }
}

/**
 * @param {number} section
 * @param {number} index
 * @param {!Array<Page>} pages
 * @returns {boolean}
 */
function isPageSectionSkipped(section, index, pages) {
  if (index < pages.length && pages[index].section == section) {
    return (pages[index].skipped && pages[index].disabled);
  } else {
    return false;
  }
}

/**
 * @param {number} index
 * @param {!Array<Page>} pages
 * @returns {number}
 */
function getNextUnskippedIndex(index, pages) {
  while (index < pages.length && pages[index].skipped) {
    index = index + 1;
  }

  return index;
}

/**
 * @param {number} index
 * @param {!Array<Page>} pages
 * @returns {number}
 */
function getPrevUnskippedIndex(index, pages) {
  while (index >= 0 && pages[index].skipped) {
    index = index - 1;
  }

  return index;
}

/**
 * @param {!Array<Page>} pages
 * @returns {!Map<string, string>}
 */
function getPageStates(pages) {
  let firstUnavailableIndex = pages.findIndex(p => p.disabled && !p.skipped);
  if (firstUnavailableIndex < 0) {
    firstUnavailableIndex = pages.length;
  }

  return new Map(pages.map((p, index) => {
    let state;
    if (p.disabled) {
      state = 'disabled';
    } else if (p.skipped) {
      state = 'skipped';
    } else {
      state = 'active';
    }

    // Alter any states that exist after the first disabled and unskipped step to disabled. This
    // will prevent the user from continuing after returning to a skipped step.
    if (firstUnavailableIndex > index) {
      return [p.id, state];
    } else {
      return [p.id, 'disabled'];
    }
  }));
}

/**
 * @param {!Array<!Array<string>>} pageIds
 * @returns {!Array<Page>}
 */
function getPages(pageIds) {
  const pages = pageIds.flatMap((ids, index) =>
    ids.map(id => ({id, section: index, disabled: true, skipped: false})),
  );

  // All pages start disabled, except for the actual page we are starting on.
  pages[0].disabled = false;
  return pages;
}

/**
 * @dict
 * @typedef {{
 *   id: string,
 *   section: number,
 *   disabled: boolean,
 *   skipped: boolean,
 * }}
 */
let Page;
